import React, { useState } from 'react';
import { theme } from '../../config/theme';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import {
    INSTALL_DISMISS_KEY,
    isInstallDismissed,
    readStorage,
    writeStorage,
} from '../../utils/pwaHelpers';

const styles = {
    banner: {
        position: 'fixed',
        left: '0.75rem',
        right: '0.75rem',
        bottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)',
        zIndex: 2500,
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
        maxWidth: '32rem',
        margin: '0 auto',
        padding: '0.85rem 1rem',
        backgroundColor: theme.colors.primary,
        color: theme.colors.textPrimary,
        border: '1px solid rgba(246, 247, 246, 0.18)',
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(13, 26, 41, 0.35)',
        fontFamily: theme.typography.fontFamily,
    },
    icon: { width: '44px', height: '44px', borderRadius: '10px', flexShrink: 0 },
    text: { flex: 1, minWidth: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.2rem' },
    title: { fontFamily: theme.typography.titleFamily, fontWeight: 700, fontSize: '0.95rem' },
    detail: { fontSize: '0.8rem', color: '#C9CFD9', lineHeight: 1.35 },
    actions: { display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 },
    installButton: {
        fontFamily: theme.typography.fontFamily,
        fontWeight: 700,
        fontSize: '0.85rem',
        minHeight: '40px',
        padding: '0 1rem',
        border: 'none',
        borderRadius: theme.borderRadius,
        backgroundColor: theme.colors.background,
        color: theme.colors.primary,
        cursor: 'pointer',
    },
    closeButton: {
        width: '40px',
        height: '40px',
        border: 'none',
        background: 'transparent',
        color: theme.colors.textSecondaryOnDark,
        fontSize: '1.5rem',
        lineHeight: 1,
        cursor: 'pointer',
    },
};

// Ícono "Compartir" de Safari, para que la instrucción sea reconocible.
const ShareIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        style={{ verticalAlign: '-2px' }}>
        <path d="M12 3v12" />
        <path d="M7 8l5-5 5 5" />
        <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
);

/**
 * Aviso para instalar la planilla como app en el celular. Aparece solo en
 * dispositivos táctiles, cuando la app todavía no está instalada. Si el usuario
 * lo cierra, no vuelve a aparecer por un mes.
 */
const InstallAppBanner = () => {
    const isTouchDevice = useMediaQuery('(pointer: coarse)');
    const { canInstall, showIosInstructions, promptInstall } = useInstallPrompt();
    const [dismissed, setDismissed] = useState(() => isInstallDismissed(readStorage(INSTALL_DISMISS_KEY)));

    if (!isTouchDevice || dismissed || (!canInstall && !showIosInstructions)) return null;

    const handleDismiss = () => {
        writeStorage(INSTALL_DISMISS_KEY, String(Date.now()));
        setDismissed(true);
    };

    const handleInstall = async () => {
        const accepted = await promptInstall();
        if (!accepted) handleDismiss();
    };

    return (
        <div style={styles.banner} role="dialog" aria-label="Instalar la planilla en el celular">
            <img src="/icon-192.png" alt="" style={styles.icon} />
            <p style={styles.text}>
                <span style={styles.title}>Instalá la planilla en tu celular</span>
                {canInstall ? (
                    <span style={styles.detail}>Se abre desde un ícono, a pantalla completa y sin la barra del navegador.</span>
                ) : (
                    <span style={styles.detail}>
                        Tocá <ShareIcon /> <strong>Compartir</strong> y después <strong>“Agregar a inicio”</strong>.
                    </span>
                )}
            </p>
            <div style={styles.actions}>
                {canInstall && (
                    <button type="button" style={styles.installButton} onClick={handleInstall}>
                        Instalar
                    </button>
                )}
                <button type="button" style={styles.closeButton} onClick={handleDismiss} aria-label="Cerrar aviso">
                    &times;
                </button>
            </div>
        </div>
    );
};

export default InstallAppBanner;
